import { useCallback } from "react";
import { useAtom } from "jotai";
import CategoryIcon from "@mui/icons-material/Category";
import {
  Alert,
  Box,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  List,
  ListSubheader,
  ListItemButton,
  ListItemIcon,
  Checkbox,
  FormControlLabel,
  ListItemText,
  Chip,
  ListItem,
  InputLabel,
  Divider,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { sessionAtom } from "../../../../features/accounts/account.state";
import { ExhaustionWall } from "../../../../components/dialogs/ExhaustionWall";
import {
  getPlanExpansion,
  getTierForProducts,
} from "../../../../components/dialogs/exhaustion-helpers";
import { BaseDataTypeNames } from "../../../../features/data/data-types";
import {
  CsvResult,
  CsvImportOptions,
  MetaRecord,
  CategoryRecord,
  FieldRecord,
  APPROVE_SECTION_ID,
  countNotApproved,
} from "../../../../features/import/csv";
import { CategorySelect } from "../../categories/CategorySelect";
import { ImportCsvEssentials } from "./import-csv-essentials";

/**
 * What happens to values a product already has where the file has an empty cell. Shown on the
 * analysis screen and on the preview: a file with nothing to resolve goes straight to the
 * preview, and this was the one thing it never got to say.
 */
export const ClearedValuesNotice = ({
  result,
  options,
  setOptions,
}: {
  result: CsvResult;
  options: CsvImportOptions;
  setOptions: (o: CsvImportOptions) => void;
}) => {
  const count = result?.analysis?.clearedValues ?? 0;
  if (count === 0) return null;
  const keepExisting = options.ignoreEmptyCells ?? true;
  return (
    <Alert severity={keepExisting ? "info" : "warning"} sx={{ my: 1 }}>
      <strong>
        {count} existing value{count === 1 ? "" : "s"} will be{" "}
        {keepExisting ? "kept" : "erased"}.
      </strong>{" "}
      These columns are in your file but left empty for products that already
      have a value.
      <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
        {result.analysis.clearedFields?.map((f) => (
          <li key={f.name}>
            <strong>{f.name}</strong> — {f.count} product
            {f.count === 1 ? "" : "s"}
          </li>
        ))}
      </Box>
      <FormControlLabel
        sx={{ mt: 1, display: "flex" }}
        control={
          <Checkbox
            size="small"
            checked={keepExisting}
            onChange={(e) =>
              setOptions({ ...options, ignoreEmptyCells: e.target.checked })
            }
          />
        }
        label="Keep the existing value where my file has an empty cell"
      />
      {!keepExisting && (
        <Box>
          An empty cell will clear the field. Names and product images are
          never cleared this way.
        </Box>
      )}
    </Alert>
  );
};

export const ImportCsvAnalysis = ({
  result,
  setResult,
  options,
  setOptions,
  returnUrl,
  onUpgradeClick,
  stale,
}: {
  result: CsvResult;
  setResult: (r: CsvResult) => void;
  options: CsvImportOptions;
  setOptions: (o: CsvImportOptions) => void;
  /** Where checkout brings the user back to, and what to do with the file first. See `ImportCsvModal`. */
  returnUrl?: string;
  onUpgradeClick?: () => void;
  /** The choices above changed since this analysis: what it found below no longer applies. */
  stale?: boolean;
}) => {
  const [session] = useAtom(sessionAtom);

  // Size the wall to the file, as S79 does for a Shopify store. The plain wall names the next tier at its
  // base limit, so a 616-row CSV on Free was quoted "Pro, 500 products": both Pro converts of 09-27..10-01
  // (616 and 578 rows) paid $49 and met this wall again on Pro within minutes (S98 pass 1). A plan whose
  // own slider reaches the file keeps the wall's expansion offer instead.
  const itemLimit = session.account.limits.items;
  const csvTotal = itemLimit + (result.analysis.limitExceeded ?? 0);
  const csvTier =
    result.analysis.limitExceeded > 0 &&
    !getPlanExpansion(session.account.planCode, "products", itemLimit)
      ? getTierForProducts(session.account.planCode, csvTotal)
      : null;
  const sizedTier = csvTier && !csvTier.short ? csvTier : null;

  const categoryMode = options.categoryMode ?? "existing";
  const notApproved = countNotApproved(result);
  const skippedProducts = result.analysis.categories
    .filter((c) => c.isNew && !c.checked)
    .reduce((n, c) => n + c.count, 0);
  // A column with no header cannot be picked: its empty value is the "none" choice of each list.
  const columnHeaders = result.analysis.headers.filter((h) => !!h);

  const updateRecord = useCallback(
    (list: MetaRecord[], record: MetaRecord, checked?: boolean) => {
      const r = record ? { ...record } : null;
      for (let i = 0; i < list.length; i++) {
        list[i] =
          list[i].name === record?.name
            ? r
            : { ...list[i], checked: checked ? true : list[i].checked };
      }
      return r;
    },
    [],
  );

  const updateCategory = useCallback(
    (
      cat: CategoryRecord,
      field?: FieldRecord,
      option?: MetaRecord,
      checked?: boolean,
    ) => {
      const categories = [...result.analysis.categories];
      const c = updateRecord(categories, cat) as CategoryRecord;
      c.fields = [...c.fields];
      c.options = [...c.options];
      updateRecord(c.fields, field, checked);
      updateRecord(c.options, option, checked);
      setResult({ ...result, analysis: { ...result.analysis, categories } });
    },
    [result, setResult, updateRecord],
  );

  const updateCollection = useCallback(
    (col: MetaRecord) => {
      const collections = [...result.analysis.collections];
      updateRecord(collections, col);
      setResult({ ...result, analysis: { ...result.analysis, collections } });
    },
    [result, setResult, updateRecord],
  );

  const approveAll = useCallback(() => {
    const categories = result.analysis.categories.map((c) => ({
      ...c,
      checked: true,
      fields: c.fields.map((f) => ({ ...f, checked: true })),
      options: c.options.map((o) => ({ ...o, checked: true })),
    }));
    const collections = result.analysis.collections.map((c) => ({
      ...c,
      checked: true,
    }));
    setResult({
      ...result,
      analysis: { ...result.analysis, categories, collections },
    });
  }, [result, setResult]);

  return (
    <Box>
      <Box my={1}>Records: {result.analysis.recordsCount}</Box>{" "}
      <Typography
        variant="caption"
        component="div"
        sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}
      >
        <span>Headers:</span>
        {result.analysis.headers.map((h, i) => [
          <i key={i + "-"}>|</i>,
          <i key={i}>{h}</i>,
        ])}
        <i>|</i>
      </Typography>
      <ImportCsvEssentials
        result={result}
        options={options}
        setOptions={setOptions}
      />
      {result.analysis.limitExceeded > 0 && (
        <ExhaustionWall
          type="products"
          current={session.account.limits.items + result.analysis.limitExceeded}
          limit={session.account.limits.items}
          mode="inline"
          entryPoint="csv-import-analysis"
          // Custom title — without this, ExhaustionWall renders the generic
          // accomplishment text "You've created N products!" which is wrong
          // for the CSV-preview case (the user hasn't actually created
          // anything; this is what the import would do).
          title={`This file would exceed your ${session.account.limits.items}-product plan`}
          subtitle={`${result.analysis.limitExceeded} of ${csvTotal} items can't be imported on your current plan.${
            sizedTier
              ? ` ${sizedTier.name} with ${sizedTier.products.toLocaleString()} products imports all of them.`
              : ""
          }`}
          targetPlan={sizedTier?.id}
          targetProducts={sizedTier?.products}
          returnUrl={returnUrl}
          onUpgradeClick={onUpgradeClick}
        />
      )}
      {result.analysis.codeHeaderAbsent && (
        <Alert severity="warning">
          <Box>
            The column header <strong>Code</strong> is not found. The system
            will automatically generate Code as a unique product id.
          </Box>
          <FormControl sx={{ my: 2 }}>
            <Select
              sx={{ minWidth: 150 }}
              value={options.codeField ?? ""}
              onChange={(event) => {
                setOptions({
                  ...options,
                  codeField: event.target.value,
                });
              }}
              labelId="code-select"
              size="small"
              displayEmpty
            >
              <MenuItem value="">
                -- Auto-generate code (or select field) --
              </MenuItem>
              {columnHeaders.map((h, i) => (
                <MenuItem key={i} value={h}>
                  {h}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Alert>
      )}
      {result.analysis.missedName > 0 && (
        <Alert severity={result.analysis.nameUnresolved ? "error" : "warning"}>
          <Box>
            {result.analysis.nameUnresolved ? (
              <>
                No product <strong>Name</strong> column was detected. Choose which
                column holds the product name, otherwise every product would be
                named after its code (1, 2, 3&hellip;).
              </>
            ) : (
              <>
                Detected <strong>{result.analysis.missedName}</strong> record(s)
                with missing product <strong>Name</strong> field.
              </>
            )}
          </Box>

          <FormControl sx={{ my: 2 }}>
            <Select
              sx={{ minWidth: 150 }}
              value={options.nameField ?? ""}
              onChange={(event) => {
                setOptions({
                  ...options,
                  nameField: event.target.value,
                  // Selecting a name column supersedes the code-as-name choice.
                  useCodeAsName: event.target.value
                    ? false
                    : options.useCodeAsName,
                });
              }}
              labelId="name-select"
              size="small"
              displayEmpty
            >
              <MenuItem value="">
                {result.analysis.nameUnresolved
                  ? "-- Select the product name column --"
                  : "-- Assign code as name (or select field) --"}
              </MenuItem>
              {columnHeaders.map((h, i) => (
                <MenuItem key={i} value={h}>
                  {h}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {result.analysis.nameUnresolved && !options.nameField && (
            <FormControlLabel
              sx={{ display: "flex", mt: 1 }}
              control={
                <Checkbox
                  checked={options.useCodeAsName ?? false}
                  onChange={(e) =>
                    setOptions({
                      ...options,
                      useCodeAsName: e.target.checked,
                    })
                  }
                />
              }
              label="My products have no names - use the product code as the name"
            />
          )}
        </Alert>
      )}
      {result.analysis.missedCategories > 0 && (
        <Alert severity="warning">
          <Box mb={1}>
            Detected <strong>{result.analysis.missedCategories}</strong> new
            product(s) with missing category. Choose where they go.
          </Box>

          <RadioGroup
            value={categoryMode}
            onChange={(e) =>
              setOptions({
                ...options,
                categoryMode: e.target.value as CsvImportOptions["categoryMode"],
              })
            }
          >
            <FormControlLabel
              value="existing"
              control={<Radio size="small" />}
              label="A category I already have"
            />
            {categoryMode === "existing" && (
              <Box sx={{ ml: 4, mb: 1 }}>
                <CategorySelect
                  categoryId={options.categoryId ?? ""}
                  onChange={(id) => {
                    setOptions({
                      ...options,
                      categoryId: id,
                    });
                  }}
                />
              </Box>
            )}
            <FormControlLabel
              value="new"
              control={<Radio size="small" />}
              label="A new category"
            />
            {categoryMode === "new" && (
              <Box sx={{ ml: 4, mb: 1 }}>
                <TextField
                  size="small"
                  autoFocus
                  placeholder="Category name"
                  value={options.newCategory ?? ""}
                  onChange={(e) =>
                    setOptions({ ...options, newCategory: e.target.value })
                  }
                  slotProps={{ htmlInput: { maxLength: 100 } }}
                  sx={{ minWidth: 250, bgcolor: "background.paper" }}
                />
              </Box>
            )}
            <FormControlLabel
              value="column"
              control={<Radio size="small" />}
              label="A column of my file holds the category"
            />
            {categoryMode === "column" && (
              <FormControl sx={{ ml: 4, mb: 1 }}>
                <Select
                  sx={{ minWidth: 150 }}
                  value={options.categoryColumn ?? ""}
                  onChange={(event) => {
                    setOptions({
                      ...options,
                      categoryColumn: event.target.value,
                    });
                  }}
                  size="small"
                  displayEmpty
                >
                  <MenuItem value="">-- Select the category column --</MenuItem>
                  {columnHeaders.map((h, i) => (
                    <MenuItem key={i} value={h}>
                      {h}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {categoryMode === "column" && !!options.categoryColumn && (
              <Box sx={{ ml: 4, mb: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Rows with nothing in that column go to:
                </Typography>
                <CategorySelect
                  categoryId={options.fallbackCategoryId ?? ""}
                  defaultLabel="Skip these rows"
                  onChange={(id) =>
                    setOptions({ ...options, fallbackCategoryId: id })
                  }
                />
              </Box>
            )}
          </RadioGroup>
        </Alert>
      )}
      {result.analysis.missedCode > 0 && (
        <Alert severity="warning">
          Detected <strong>{result.analysis.missedCode}</strong> record(s) with
          missing Code field. The system will automatically generate Code for
          new products.
        </Alert>
      )}
      {result.analysis.productMoved > 0 && (
        <Alert severity="warning">
          Detected <strong>{result.analysis.productMoved}</strong> product(s)
          moved to another category.
        </Alert>
      )}
      <ClearedValuesNotice
        result={result}
        options={options}
        setOptions={setOptions}
      />
      {stale && (
        <Alert severity="info">
          Click <strong>Continue</strong> to read the file again with your
          choices.
        </Alert>
      )}
      {!stale &&
        (result.analysis.categoryChanged ||
          result.analysis.collections.length > 0) && (
        <Alert
          id={APPROVE_SECTION_ID}
          severity={notApproved > 0 ? "warning" : "info"}
          sx={{ scrollMarginTop: 8 }}
        >
          Detected new fields or collections in your file. Do you want to update
          your account for these changes?
          {skippedProducts > 0 && (
            <Box mt={1}>
              <strong>{skippedProducts}</strong> product(s) are in a new category
              that is not ticked below. They are not imported until you tick it.
            </Box>
          )}
          <Box mt={2}>
            <Button variant="outlined" size="small" onClick={approveAll}>
              Approve & select all updates
            </Button>
          </Box>
          {result.analysis.categoryAdded > 1 && (
            <Box mt={2}>
              You added <strong>{result.analysis.categoryAdded}</strong> new
              categories. Consider using <strong>collections</strong> to group
              your products.
            </Box>
          )}
        </Alert>
      )}
      {!stale && result.analysis.categoryChanged && (
        <Box>
          <List
            subheader={
              <ListSubheader>
                Select categories and fields for adding
              </ListSubheader>
            }
          >
            {result.analysis.categories.map((cat, i) => (
              <Box key={i}>
                <Grid container>
                  <Grid size={{ xs: 6 }}>
                    <ListItemButton key={cat.name}>
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          disableRipple
                          checked={cat.checked ?? false}
                          onChange={(e) =>
                            updateCategory(
                              {
                                ...cat,
                                checked: e.target.checked,
                              },
                              null,
                              null,
                              e.target.checked,
                            )
                          }
                        />
                      </ListItemIcon>
                      <ListItemText
                        sx={{ my: 0 }}
                        primary={
                          <Box display="flex" alignItems="center" pb={1}>
                            <CategoryIcon fontSize="small" sx={{ mr: 1 }} />
                            <span>{cat.name}</span>
                            {cat.isNew && (
                              <Chip label="NEW" sx={{ ml: 1 }} size="small" />
                            )}
                          </Box>
                        }
                        secondary={cat.count + " products in the category"}
                      />
                    </ListItemButton>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    {cat.checked && cat.fields.length > 0 && (
                      <List dense>
                        <Typography variant="caption">Add Fields</Typography>
                        {cat.fields.map((f, i) => (
                          <ListItem
                            key={i}
                            sx={{ my: 1 }}
                            secondaryAction={
                              f.checked && (
                                <FormControl sx={{ minWidth: 100 }}>
                                  <InputLabel>Data Type</InputLabel>
                                  <Select
                                    value={f.dataType}
                                    onChange={(e) =>
                                      updateCategory(cat, {
                                        ...f,
                                        dataType: e.target.value,
                                      })
                                    }
                                    label="Data Type"
                                    size="small"
                                  >
                                    {BaseDataTypeNames.map((t) => (
                                      <MenuItem key={t} value={t}>
                                        {t}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )
                            }
                            disablePadding
                          >
                            <ListItemButton>
                              <ListItemIcon>
                                <Checkbox
                                  edge="start"
                                  disableRipple
                                  checked={f.checked ?? false}
                                  onChange={(e) =>
                                    updateCategory(cat, {
                                      ...f,
                                      checked: e.target.checked,
                                    })
                                  }
                                />
                              </ListItemIcon>
                              <ListItemText primary={f.name} />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    )}
                    {cat.checked && cat.options.length > 0 && (
                      <List dense disablePadding>
                        <Typography variant="caption">
                          Add Variant Options
                        </Typography>
                        {cat.options.map((o, i) => (
                          <ListItem key={i} disablePadding>
                            <ListItemButton>
                              <ListItemIcon>
                                <Checkbox
                                  edge="start"
                                  disableRipple
                                  checked={o.checked ?? false}
                                  onChange={(e) =>
                                    updateCategory(cat, null, {
                                      ...o,
                                      checked: e.target.checked,
                                    })
                                  }
                                />
                              </ListItemIcon>
                              <ListItemText primary={o.name} />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Grid>
                </Grid>{" "}
                <Divider />
              </Box>
            ))}
          </List>
        </Box>
      )}
      {!stale && result.analysis.collections.length > 0 && (
        <Box>
          <List
            subheader={
              <ListSubheader>Select new collections for adding</ListSubheader>
            }
          >
            {result.analysis.collections.map((c, i) => (
              <Box key={i}>
                <ListItem key={c.name} disablePadding>
                  <ListItemButton>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        disableRipple
                        checked={c.checked ?? false}
                        onChange={(e) =>
                          updateCollection({
                            ...c,
                            checked: e.target.checked,
                          })
                        }
                      />
                    </ListItemIcon>
                    <ListItemText primary={c.name} />
                  </ListItemButton>
                </ListItem>
              </Box>
            ))}
          </List>
        </Box>
      )}
      {result.errors?.length > 0 && (
        <Box my={1}>
          <Alert severity="error">
            File contains{" "}
            {result.errorCount > 0 && <span>{result.errorCount} errors</span>}{" "}
            {result.warningCount > 0 && (
              <span>{result.warningCount} warnings</span>
            )}
          </Alert>
          <Table
            sx={{
              maxHeight: 100,
              my: 1,
              overflowY: "auto",
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Line</TableCell>
                <TableCell>Code</TableCell>

                <TableCell>Error</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.errors.map((e, i) => (
                <TableRow key={i}>
                  <TableCell>{e.index}</TableCell>
                  <TableCell>{e.code}</TableCell>

                  <TableCell>
                    <Typography
                      color={e.isWarning ? "warning" : "error"}
                      component="span"
                    >
                      {e.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {e.count > 0 && <span>+ {e.count} record(s)</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};
